import React, { memo, useCallback, useMemo, useRef, useState, useEffect } from "react";
import moment from "moment";

import { formatDateKey, generateCalendarDays, isToday, METRIC, getMetricColor, getQuantityTotal } from "./calendarUtils";

import { CalendarHeader } from "../CalendarHeader";
import DateCell from "./DateCell";
import WeekDayRow from "./WeekDayRow";
import "./MonthCalendar.scss";

const GAP_SIZE = 12;
const NUM_COLUMNS = 7;

const MonthCalendar = ({
  currentDate,
  selectedDates,
  onDateSelect,
  onMonthChange,
  onYearSelect,
  payRequestData,
  inputMode,
  inputValues,
  onInputChange,
  onInputFocus,
  isLoading = false,
  metricType,
  disabledDates,
  disableFutureDate = false,
  workCalendarData,
  decimalInput = false,
  showThresolds = false,
  thresholdUnitType = null,
}) => {
  const gridRef = useRef(null);
  const [cellWidth, setCellWidth] = useState(60);


  useEffect(() => {
    const updateCellWidth = () => {
      if (gridRef.current) {
        const containerWidth = gridRef.current.offsetWidth;
        const totalGapWidth = GAP_SIZE * (NUM_COLUMNS - 1);
        const calculatedWidth = (containerWidth - totalGapWidth) / NUM_COLUMNS;
        setCellWidth(calculatedWidth);
      }
    };

    updateCellWidth();
    window.addEventListener("resize", updateCellWidth);
    return () => window.removeEventListener("resize", updateCellWidth);
  }, []);

  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth();

  const days = useMemo(() => generateCalendarDays(currentYear, currentMonth), [currentYear, currentMonth]);

  const anyDateSelected = selectedDates.size > 0;

  const buildDayData = useCallback(
    (day) => {
      const dateKey = formatDateKey(day.date);
      const data = payRequestData?.get(dateKey);
      const dayMoment = moment(day.date).startOf("day");
      const today = moment().startOf("day");
      const isFutureDate = disableFutureDate && dayMoment.isAfter(today);

      let metricValue = null;
      let hasData = false;
;

      if (data) {
        if (metricType.value === METRIC.HOURS) {
          const hours = data.hours || 0;
          const minutes = data.minutes || 0;
          metricValue = `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
        } else if (metricType.value === METRIC.TOTAL_AMOUNT) {
          metricValue = data.budget || 0;
        } else if (metricType.value === METRIC.COUNT) {
          metricValue = data.timesheetCount || 0;
        } else if (metricType.value === METRIC.QUANTITY) {
          metricValue = getQuantityTotal(data.quantity);
        }
        hasData = true;
      }

      return {
        date: dateKey,
        dayNumber: day.date.getDate(),
        isCurrentMonth: day.isCurrentMonth,
        isToday: isToday(day.date),
        isSelected: selectedDates.has(dateKey),
        hasPayRequest: hasData,
        metricValue,
        metricColor: getMetricColor(metricType),
        // Padding days from the neighbouring months are never selectable, so
        // they carry the disabled treatment too. Without this the trailing row
        // (disabled via isFutureDate) and the leading row rendered as two
        // different shades of the same non-interactive state.
        isDisabled: (disabledDates?.has(dateKey) ?? false) || isFutureDate || !day.isCurrentMonth,
      };
    },
    [payRequestData, selectedDates, metricType, disabledDates, disableFutureDate]
  );

  const dayDataList = useMemo(() => days.map(buildDayData), [days, buildDayData]);

  return (
    <div className="month-calendar">
      <CalendarHeader
        currentDate={currentDate}
        onMonthChange={onMonthChange}
        onYearSelect={onYearSelect}
        metricType={metricType}
        isLoading={isLoading}
        showThresholds={showThresolds}
        thresholdUnitType={thresholdUnitType}
      />

      <WeekDayRow cellWidth={cellWidth} />

      <div className="month-calendar__grid" ref={gridRef}>
        {dayDataList.map((dayData) => (
          <DateCell
            key={dayData.date}
            day={dayData}
            inputMode={inputMode}
            inputValue={inputValues?.get(dayData.date) ?? ""}
            onPress={onDateSelect}
            onInputChange={onInputChange}
            onInputFocus={onInputFocus}
            anyDateSelected={anyDateSelected}
            cellWidth={cellWidth}
            metricType={metricType}
            metricColor={dayData.metricColor}
            decimalInput={decimalInput}
          />
        ))}
      </div>
    </div>
  );
};

export default memo(MonthCalendar);
